<template>
  <div class="state-page">
    <h2>检测客户位置 - {{ locationName }}</h2>
    <div class="chart-container">
      <div
        v-for="(sensor, idx) in sensorGroups"
        :key="sensor.id"
        class="sensor-card"
        :class="{
          alert: sensor.doorStatus === 'closed',
          offline: sensor.isOffline
        }"
      >
        <!-- 顶部区 -->
        <div class="card-header">
          <span class="sensor-id">ID: {{ sensor.id }}</span>
          <span class="sensor-name">{{ sensor.name }}</span>
          <span v-if="sensor.isOffline" class="status-badge offline-badge">离线</span>
          <span v-else class="status-badge online-badge">在线</span>
        </div>
        <!-- 信息区 -->
        <div class="card-info">
          <div class="info-item">
            <span class="material-symbols-rounded info-icon">qr_code_2</span>
            <span class="info-label">IMEI</span>
            <span class="info-value">{{ sensor.imei }}</span>
          </div>
          <div class="info-item">
            <span class="material-symbols-rounded info-icon">wifi</span>
            <span class="info-label">MQTT</span>
            <span class="info-value">{{ sensor.mqttTopic }}</span>
          </div>
          <div class="info-item">
            <span class="material-symbols-rounded info-icon">signal_cellular_alt</span>
            <span class="info-label">设备信号</span>
            <span class="info-value">{{ sensor.signal }}</span>
          </div>
          <div class="info-item">
            <span class="material-symbols-rounded info-icon">battery_full</span>
            <span class="info-label">检测设备电源</span>
            <span class="info-value">{{ sensor.battery }} mV</span>
          </div>
        </div>
        <!-- 电压区 -->
        <div class="card-voltage">
          <div class="voltage-item">
            <span class="voltage-label">供电</span>
            <span class="voltage-value">{{ (sensor.powerVoltage / 1000).toFixed(3) }} V</span>
          </div>
          <div class="voltage-item">
            <span class="voltage-label">门禁</span>
            <span class="voltage-value">{{ (sensor.accessControlVoltage / 1000).toFixed(3) }} V</span>
          </div>
          <div class="voltage-item">
            <span class="voltage-label">外感应</span>
            <span class="voltage-value">{{ (sensor.externalHumanSensorVoltage / 1000).toFixed(3) }} V</span>
          </div>
          <div class="voltage-item">
            <span class="voltage-label">内感应</span>
            <span class="voltage-value">{{ (sensor.internalHumanSensorVoltage / 1000).toFixed(3) }} V</span>
          </div>
        </div>
        <!-- 状态区 -->
        <div class="card-status">
          <!-- 开门状态 -->
          <div class="status-block">
            <span :class="['status-led', sensor.in1Status === 0 ? 'led-open' : 'led-closed']"></span>
            <span class="material-symbols-rounded status-icon">door_open</span>
            <span class="status-label">开门</span>
          </div>
          <!-- 关门状态 -->
          <div class="status-block">
            <span :class="['status-led', sensor.in2Status === 0 ? 'led-open' : 'led-closed']"></span>
            <span class="material-symbols-rounded status-icon">door_sliding</span>
            <span class="status-label">关门</span>
          </div>
          <!-- 门禁卡状态 -->
          <div class="status-block">
            <span :class="['status-led', sensor.cardStatus === 'active' ? 'led-open' : 'led-closed']"></span>
            <span class="material-symbols-rounded status-icon">vpn_key</span>
            <span class="status-label">{{ sensor.cardStatus === 'active' ? '门禁正常' : '门禁关闭' }}</span>
            <span v-if="sensor.cardStatus === 'inactive'" class="status-x">✖</span>
          </div>
          <!-- 人体感应器状态 -->
          <div class="status-block">
            <span :class="['status-led', sensor.humanStatus === 'active' ? 'led-open' : 'led-closed']"></span>
            <span class="material-symbols-rounded status-icon">sensor_occupied</span>
            <span class="status-label">{{ sensor.humanStatus === 'active' ? '感应正常' : '感应关闭' }}</span>
            <span v-if="sensor.humanStatus === 'inactive'" class="status-x">✖</span>
          </div>
          <!-- 设置按钮 (管理员和客户用户共用) -->
          <span v-if="canManageSensors" class="material-symbols-rounded settings-icon" @click="openSettings(idx)">settings</span>
        </div>
      </div>
      <!-- 添加新传感器卡片 (管理员和客户用户可用) -->
      <div v-if="canManageSensors" class="chart-group add-card" @click="openAddSensorDialog">
        <div class="add-icon">+</div>
        <div class="add-text">添加自动门传感器</div>
      </div>
    </div>
    <!-- 设置弹窗 -->
    <div v-if="settingsDialog.visible" class="settings-overlay">
      <div class="settings-dialog">
        <h3>设置卡片</h3>
        <div class="setting-item">
          <label>卡片名称</label>
          <input v-model="settingsDialog.editName" />
        </div>
        <div class="setting-item" v-for="(label, idx) in settingsDialog.editVoltageLabels" :key="idx">
          <label>检测项{{ idx + 1 }}名称</label>
          <input v-model="settingsDialog.editVoltageLabels[idx]" style="width: 60%" />
          <span style="margin-left:8px;">范围：</span>
          <input type="number" v-model.number="settingsDialog.editVoltageRanges[idx][0]" style="width:60px;" placeholder="最小" />
          <span>~</span>
          <input type="number" v-model.number="settingsDialog.editVoltageRanges[idx][1]" style="width:60px;" placeholder="最大" />
        </div>
        <div class="dialog-actions">
          <button @click="saveSettings">保存</button>
          <button @click="closeSettings">取消</button>
        </div>
      </div>
    </div>
    <!-- 编辑传感器弹窗 -->
    <div v-if="editSensorDialog.visible" class="dialog-mask">
      <div class="dialog">
        <h3>编辑自动门传感器</h3>
        <div style="margin-bottom:8px; color:#888;">检测点ID：<input v-model="editSensorDialog.sensor.locationId" type="number" readonly /></div>
        <input v-model="editSensorDialog.sensor.name" placeholder="检测点名称" style="margin-bottom:8px;" />
        <input v-model="editSensorDialog.sensor.imei" placeholder="IMEI编号" maxlength="15" style="margin-bottom:8px;" />
        <input v-model="editSensorDialog.sensor.mqttTopic" placeholder="MQTT Topic" style="margin-bottom:8px;" />
        <div style="margin-bottom:8px;">
          门状态：
          <select v-model="editSensorDialog.sensor.doorStatus">
            <option value="open">open</option>
            <option value="closed">closed</option>
          </select>
        </div>
        <div style="margin-bottom:8px;">
          门禁卡状态：
          <select v-model="editSensorDialog.sensor.cardStatus">
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </select>
        </div>
        <div style="margin-bottom:8px;">
          人体感应器状态：
          <select v-model="editSensorDialog.sensor.humanStatus">
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </select>
        </div>
        <div style="color:#00f3ff; font-size:0.98em; margin-bottom:4px;">检测点供电额定电压为24V</div>
        <input v-model.number="editSensorDialog.sensor.voltages.voltage1" placeholder="电压1" style="margin-bottom:8px;" />
        <input v-model="editSensorDialog.sensor.voltageLabels.label1" placeholder="电压标签1" style="margin-bottom:8px;" />
        <div style="color:#00f3ff; font-size:0.98em; margin-bottom:2px;">最低电压报警值</div>
        <input v-model.number="editSensorDialog.sensor.voltageRanges.range1.min" placeholder="电压范围最小" style="width:48%;margin-bottom:8px;" />
        <div style="color:#00f3ff; font-size:0.98em; margin-bottom:2px;">最高电压报警值</div>
        <input v-model.number="editSensorDialog.sensor.voltageRanges.range1.max" placeholder="电压范围最大" style="width:48%;margin-bottom:8px;" />
        <div class="dialog-actions">
          <button @click="submitEditSensor">保存</button>
          <button @click="openDeleteDialog(editSensorDialog.sensor.id)" style="background:#ff4757;color:white">删除</button>
          <button @click="editSensorDialog.visible = false">取消</button>
        </div>
      </div>
    </div>
    <!-- 加法校验对话框 -->
    <div v-if="showDeleteVerificationDialog" class="dialog-mask">
      <div class="dialog">
        <h3 style="color:#ff4757">删除校验</h3>
        <div class="setting-item">请输入答案以确认删除：{{ deleteVerification.num1 }} + {{ deleteVerification.num2 }} = ?</div>
        <input v-model.number="deleteVerification.userAnswer" type="number" placeholder="请输入答案" style="margin-bottom:8px;" />
        <div class="dialog-actions">
          <button @click="confirmDeleteSensorGroup" style="background:#ff4757;color:white">确认删除</button>
          <button @click="showDeleteVerificationDialog = false">取消</button>
        </div>
      </div>
    </div>
    <!-- 地图弹窗 -->
    <el-dialog v-model="mapVisible" title="设备位置">
      <div id="map" style="width: 100%; height: 400px;"></div>
    </el-dialog>
    <!-- 添加自动门传感器弹窗 -->
    <div v-if="showAddSensorDialog" class="dialog-mask">
      <div class="dialog">
        <h3>添加自动门传感器</h3>
        <input v-model="newSensor.name" placeholder="检测点名称" style="margin-bottom:8px;" />
        <input v-model="newSensor.imei" placeholder="IMEI编号" maxlength="15" style="margin-bottom:8px;" />
        <input v-model="newSensor.mqttTopic" placeholder="MQTT Topic" style="margin-bottom:8px;" />
        <!-- 其他需要录入的字段 -->
        <div class="dialog-actions">
          <button @click="submitAddSensor">保存</button>
          <button @click="showAddSensorDialog = false">取消</button>
        </div>
      </div>
    </div>
    <div v-if="alertMessages.length" class="alert-bar">
      <span class="alert-message">
        {{ alertMessages[alertIndex] }}
      </span>
      <span class="alert-counter">
        ({{ alertIndex + 1 }}/{{ alertMessages.length }})
      </span>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request';
import { WS_BASE_URL } from '@/config';

export default {
  name: 'StatePage',
  computed: {
    isAdmin() {
      return localStorage.getItem('userType') === 'admin';
    },
    isLocationUser() {
      return localStorage.getItem('userType') === 'location';
    },
    canManageSensors() {
      return this.isAdmin || this.isLocationUser;
    },
    currentLocationId() {
      return localStorage.getItem('locationId');
    }
  },
  data() {
    return {
      locationName: '',
      sensorGroups: [],
      offlineImeis: [],
      settingsDialog: {
        visible: false,
        editIdx: null,
        editName: '',
        editVoltageLabels: [],
        editVoltageRanges: []
      },
      deleteDialog: {
        visible: false
      },
      alertMessages: [],
      alertIndex: 0,
      alertTimer: null,
      hospitals: [],
      showAddHospitalDialog: false,
      newHospitalName: '',
      showAddSensorDialog: false,
      newSensor: {
        locationId: '', // 进入页面时赋值
        name: '',
        imei: '',
        mqttTopic: '',
        doorStatus: 'open',
        cardStatus: 'active',
        humanStatus: 'active',
        voltages: { voltage1: 24 },
        voltageLabels: { label1: 'Main Line' },
        voltageRanges: { range1: { min: 23, max: 25 } }
      },
      // WebSocket 相关
      socket: null,
      editSensorDialog: {
        visible: false,
        sensor: {
          id: null,
          locationId: '',
          name: '',
          imei: '',
          mqttTopic: '',
          doorStatus: 'open',
          cardStatus: 'active',
          humanStatus: 'active',
          voltages: { voltage1: 0 },
          voltageLabels: { label1: '' },
          voltageRanges: { range1: { min: 0, max: 0 } },
          createTime: '',
          updateTime: ''
        }
      },
      mapVisible: false,
      currentLocation: {},
      deleteVerification: {
        num1: Math.floor(Math.random() * 10) + 1,
        num2: Math.floor(Math.random() * 10) + 1,
        userAnswer: '',
      },
      showDeleteVerificationDialog: false,
    }
  },
  created() {
    // 权限检查
    const userType = localStorage.getItem('userType');
    const storedLocationId = localStorage.getItem('locationId');
    const routeLocationId = this.$route.params.sensorId;

    // 如果是客户用户，验证访问权限
    if (userType === 'location') {
      if (storedLocationId !== routeLocationId) {
        alert('无权访问该客户位置');
        this.$router.replace('/login');
        return;
      }
      // 客户用户直接从本地存储读取名称，避免调用管理员接口
      this.locationName = localStorage.getItem('locationName') || '客户位置';
    } else {
      this.loadLocationName();
    }

    this.startAlertPolling();
    this.getSensors();
  },
  beforeUnmount() {
    this.stopAlertPolling();
    if (this.alertPollingInterval) {
      clearInterval(this.alertPollingInterval);
    }
    // 关闭WebSocket连接
    if (this.socket) {
      console.log('[WebSocket] 组件销毁，关闭连接');
      this.socket.close();
      this.socket = null;
    }
  },
  methods: {
    loadLocationName() {
      const locationId = this.$route.params.sensorId;
      request.get('locations/get')
        .then(res => {
          if (res.data.code === 200) {
            const location = res.data.data.find(loc => String(loc.id) === String(locationId));
            if (location) {
              this.locationName = location.name;
            } else {
              this.locationName = '未知地点';
            }
          } else {
            this.locationName = '未知地点';
          }
        })
        .catch(() => {
          this.locationName = '未知地点';
        });
    },
    loadLocationData() {
      const locations = JSON.parse(localStorage.getItem('monitoringLocations') || '[]');
      const location = locations.find(l => l.id === this.$route.params.locationId);
      if (location) {
        this.sensorGroups = (location.sensorGroups || []).filter(sensor => sensor.id === this.$route.params.sensorId).map(sensor => ({
          name: sensor.name || '',
          voltageLabels: sensor.voltageLabels || [
            '自动门系统电压',
            '人体感应器1电压',
            '人体感应器2电压',
            '门禁刷卡器电压'
          ],
          voltages: sensor.voltages || [sensor.doorVoltage || 0, sensor.sensor1Voltage || 0, sensor.sensor2Voltage || 0, sensor.cardReaderVoltage || 0],
          voltageRanges: sensor.voltageRanges || [[0,100],[0,100],[0,100],[0,100]],
          doorStatus: sensor.doorStatus || 'closed',
          cardStatus: sensor.cardStatus || 'inactive',
          humanStatus: sensor.humanStatus || 'inactive',
          id: sensor.id || ('sensor-' + Date.now() + Math.random()),
          imei: sensor.imei || '',
          mqttTopic: sensor.mqtt_topic || '',
          powerVoltage: sensor.powerVoltage || 0,
          accessControlVoltage: sensor.accessControlVoltage || 0,
          externalHumanSensorVoltage: sensor.externalHumanSensorVoltage || 0,
          internalHumanSensorVoltage: sensor.internalHumanSensorVoltage || 0
        }));
      } else {
        this.locationName = '未知地点';
        this.sensorGroups = [];
      }
      this.checkAlerts();
    },
    addSensorGroup() {
      const newSensor = {
        id: 'sensor-' + Date.now(),
        name: '',
        voltageLabels: [
          '自动门系统电压',
          '人体感应器1电压',
          '人体感应器2电压',
          '门禁刷卡器电压'
        ],
        voltages: [0, 0, 0, 0],
        voltageRanges: [[0,100],[0,100],[0,100],[0,100]],
        doorStatus: 'closed',
        cardStatus: 'inactive',
        humanStatus: 'inactive',
        imei: '',
        mqttTopic: '',
        powerVoltage: 0,
        accessControlVoltage: 0,
        externalHumanSensorVoltage: 0,
        internalHumanSensorVoltage: 0
      };
      this.sensorGroups.push(newSensor);
      this.saveSensorGroups();
      this.checkAlerts();
    },
    saveSensorGroups() {
      const locations = JSON.parse(localStorage.getItem('monitoringLocations') || '[]');
      const idx = locations.findIndex(l => l.id === this.$route.params.locationId);
      if (idx !== -1) {
        locations[idx].sensorGroups = this.sensorGroups;
        localStorage.setItem('monitoringLocations', JSON.stringify(locations));
      }
      this.checkAlerts();
    },
    openSettings(idx) {
      const sensor = this.sensorGroups[idx];
      this.editSensorDialog.sensor = { ...sensor };
      this.editSensorDialog.visible = true;
      this.deleteDialog.sensorId = sensor.id;
      this.deleteVerification = {
        num1: Math.floor(Math.random() * 10) + 1,
        num2: Math.floor(Math.random() * 10) + 1,
        userAnswer: '',
      };
    },
    closeSettings() {
      this.settingsDialog.visible = false;
    },
    saveSettings() {
      const idx = this.settingsDialog.editIdx;
      if (idx !== null) {
        this.sensorGroups[idx].name = this.settingsDialog.editName;
        this.sensorGroups[idx].voltageLabels = [...this.settingsDialog.editVoltageLabels];
        this.sensorGroups[idx].voltageRanges = this.settingsDialog.editVoltageRanges.map(r => [...r]);
        this.sensorGroups[idx].imei = this.newSensor.imei;
        this.sensorGroups[idx].mqttTopic = this.newSensor.mqttTopic;
        this.sensorGroups[idx].powerVoltage = this.newSensor.powerVoltage;
        this.sensorGroups[idx].accessControlVoltage = this.newSensor.accessControlVoltage;
        this.sensorGroups[idx].externalHumanSensorVoltage = this.newSensor.externalHumanSensorVoltage;
        this.sensorGroups[idx].internalHumanSensorVoltage = this.newSensor.internalHumanSensorVoltage;
        this.saveSensorGroups();
      }
      this.closeSettings();
    },
    confirmDelete() {
      this.deleteDialog.visible = true;
    },
    closeDeleteDialog() {
      this.deleteDialog.visible = false;
    },
    openDeleteDialog(sensorId) {
      this.deleteDialog.sensorId = sensorId;
      this.deleteVerification = {
        num1: Math.floor(Math.random() * 10) + 1,
        num2: Math.floor(Math.random() * 10) + 1,
        userAnswer: '',
      };
      this.showDeleteVerificationDialog = true;
    },
    async confirmDeleteSensorGroup() {
      const { num1, num2, userAnswer } = this.deleteVerification;
      if (Number(userAnswer) !== num1 + num2) {
        alert('答案错误，请重新输入！');
        return;
      }
      const id = this.deleteDialog.sensorId;
      if (!id) {
        alert('未获取到要删除的ID！');
        return;
      }
      try {
        const res = await request.delete(`sensors/delete/${id}`);
        if (res.data && res.data.code === 200) {
          this.showDeleteVerificationDialog = false;
          this.editSensorDialog.visible = false;
          this.getSensors();
          alert('删除成功！');
        } else {
          alert(res.data.message || '删除失败');
        }
      } catch (e) {
        alert('网络错误或服务器无响应');
      }
    },
    checkAlerts() {
      const alerts = [];
      this.sensorGroups.forEach(sensor => {
        const min = sensor.voltageRanges?.range1?.min * 1000; // 将最小值转换为 mV
        const max = sensor.voltageRanges?.range1?.max * 1000; // 将最大值转换为 mV
        const voltageMap = [
          { key: 'powerVoltage', label: '供电' },
          { key: 'accessControlVoltage', label: '门禁' },
          { key: 'externalHumanSensorVoltage', label: '外感应' },
          { key: 'internalHumanSensorVoltage', label: '内感应' }
        ];
        voltageMap.forEach(({ key, label }) => {
          const v = Number(sensor[key]); // 设备上报的原始值（mV）
          if (!isNaN(min) && !isNaN(max) && !isNaN(v)) {
            if (v < min || v > max) {
              alerts.push(`${sensor.name} ${label}电压报警：当前值${(v / 1000).toFixed(3)}V，范围${(min / 1000).toFixed(3)}~${(max / 1000).toFixed(3)}V`);
            }
          }
        });
      });
      this.alertMessages = alerts;
      if (alerts.length) {
        this.startAlertPolling();
      } else {
        this.stopAlertPolling();
      }
    },
    startAlertPolling() {
      if (this.alertTimer) return; // 防止重复启动
      this.alertIndex = 0; // 从第一个报警信息开始
      this.alertTimer = setInterval(() => {
        if (this.alertMessages.length) {
          this.alertIndex = (this.alertIndex + 1) % this.alertMessages.length; // 循环显示报警信息
        }
      }, 10000); // 每 10 秒切换一次
    },
    stopAlertPolling() {
      if (this.alertTimer) {
        clearInterval(this.alertTimer);
        this.alertTimer = null;
      }
    },
    getHospitals() {
      request.get('locations/get')
        .then(res => {
          if (res.data.code === 200) this.hospitals = res.data.data;
        });
    },
    addHospital() {
      if (!this.newHospitalName.trim()) {
        alert('请输入客户名称');
        return;
      }
      request.post('locations/add', { name: this.newHospitalName })
        .then(res => {
          if (res.data.code === 200) {
            this.showAddHospitalDialog = false;
            this.newHospitalName = '';
            this.getHospitals();
          } else {
            alert(res.data.message || '添加失败');
          }
        })
        .catch(() => {
          alert('网络错误，添加失败');
        });
    },
    resolveActiveLocationId() {
      const routeId = this.$route.params.sensorId;
      const storedId = localStorage.getItem('locationId');
      const candidate = routeId || storedId;
      
      // 调试信息
      console.log('[State] 解析locationId:', {
        routeId,
        storedId,
        candidate,
        routeParams: this.$route.params,
        allLocalStorage: {
          locationId: localStorage.getItem('locationId'),
          locationName: localStorage.getItem('locationName'),
          userType: localStorage.getItem('userType'),
          userRole: localStorage.getItem('userRole')
        }
      });
      
      if (!candidate) {
        console.error('[State] 错误：未找到locationId，路由参数:', this.$route.params, 'localStorage:', {
          locationId: localStorage.getItem('locationId'),
          locationName: localStorage.getItem('locationName')
        });
        return null;
      }
      
      // 确保返回数字类型，如果转换失败则返回null
      const numeric = Number(candidate);
      if (Number.isFinite(numeric) && numeric > 0) {
        console.log('[State] locationId解析成功:', numeric);
        return numeric;
      }
      
      console.error('[State] locationId转换失败，值:', candidate, '类型:', typeof candidate);
      return null;
    },
    openAddSensorDialog() {
      const locationId = this.resolveActiveLocationId();
      if (!locationId) {
        alert('未获取到客户位置ID，无法添加传感器');
        return;
      }
      this.newSensor = {
        locationId,
        name: '',
        imei: '',
        mqttTopic: '',
        doorStatus: 'open',
        cardStatus: 'active',
        humanStatus: 'active',
        voltages: { voltage1: 24 },
        voltageLabels: { label1: 'Main Line' },
        voltageRanges: { range1: { min: 23, max: 25 } }
      };
      this.showAddSensorDialog = true;
    },
    /**
     * 验证JWT token是否有效
     */
    validateJWTToken() {
      const token = localStorage.getItem('jwtToken') || localStorage.getItem('authToken');
      if (!token) {
        console.error('[Token验证] 未找到token');
        return { valid: false, reason: '未找到token' };
      }
      
      try {
        const parts = token.split('.');
        if (parts.length !== 3) {
          console.error('[Token验证] Token格式不正确，部分数量:', parts.length);
          return { valid: false, reason: 'Token格式不正确' };
        }
        
        // 解析payload
        const payload = parts[1];
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
        // 使用正确的方式解码UTF-8字符（支持中文）
        let decoded;
        try {
          decoded = decodeURIComponent(escape(atob(padded)));
        } catch (e) {
          decoded = atob(padded);
        }
        const jwtPayload = JSON.parse(decoded);
        
        // 检查过期时间
        const now = Math.floor(Date.now() / 1000);
        if (jwtPayload.exp && now >= jwtPayload.exp) {
          const expiredSeconds = now - jwtPayload.exp;
          console.error('[Token验证] Token已过期:', {
            expiredAt: new Date(jwtPayload.exp * 1000).toLocaleString(),
            expiredSecondsAgo: expiredSeconds,
            expiredMinutesAgo: Math.floor(expiredSeconds / 60)
          });
          return { valid: false, reason: 'Token已过期', expiredAt: new Date(jwtPayload.exp * 1000) };
        }
        
        // 检查必要字段
        if (!jwtPayload.locationId) {
          console.warn('[Token验证] Token中缺少locationId');
        }
        if (!jwtPayload.role) {
          console.warn('[Token验证] Token中缺少role字段');
        }
        
        console.log('[Token验证] Token有效:', {
          locationId: jwtPayload.locationId,
          locationName: jwtPayload.locationName,
          role: jwtPayload.role,
          expiresAt: jwtPayload.exp ? new Date(jwtPayload.exp * 1000).toLocaleString() : '无',
          remainingSeconds: jwtPayload.exp ? (jwtPayload.exp - now) : null
        });
        
        return { valid: true, payload: jwtPayload };
      } catch (error) {
        console.error('[Token验证] Token解析失败:', error);
        return { valid: false, reason: 'Token解析失败: ' + error.message };
      }
    },

    submitAddSensor() {
      // 首先验证token
      const tokenValidation = this.validateJWTToken();
      if (!tokenValidation.valid) {
        alert('Token验证失败：' + tokenValidation.reason + '\n\n请重新登录后重试。');
        if (tokenValidation.reason.includes('过期')) {
          // Token过期，清除登录信息并跳转
          localStorage.clear();
          this.$router.replace('/login');
        }
        return;
      }
      
      // 校验
      if (!this.newSensor.name || !this.newSensor.imei || !this.newSensor.mqttTopic) {
        alert('请填写完整信息');
        return;
      }
      if (this.newSensor.imei.length > 15) {
        alert('IMEI编号不能超过15位');
        return;
      }
      
      // 确保locationId是数字类型
      let locationId = this.newSensor.locationId;
      if (!locationId) {
        locationId = this.resolveActiveLocationId();
        if (!locationId) {
          alert('未获取到客户位置ID，无法添加传感器');
          return;
        }
      }

      // 确保locationId是数字类型
      const numericLocationId = Number(locationId);
      if (!Number.isFinite(numericLocationId) || numericLocationId <= 0) {
        alert('客户位置ID无效，必须是有效的数字');
        return;
      }
      
      // 验证请求中的locationId是否与JWT中的一致（普通用户只能操作自己的位置）
      if (tokenValidation.payload && tokenValidation.payload.locationId) {
        const jwtLocationId = Number(tokenValidation.payload.locationId);
        if (jwtLocationId !== numericLocationId) {
          console.warn('[提交] locationId不匹配:', {
            jwtLocationId,
            requestLocationId: numericLocationId
          });
          // 对于普通用户，使用JWT中的locationId
          if (localStorage.getItem('userType') === 'location') {
            console.log('[提交] 使用JWT中的locationId:', jwtLocationId);
            locationId = jwtLocationId;
          }
        }
      }
      
      // 只发送后端文档要求的4个字段
      const payload = {
        locationId: Number(locationId),
        name: this.newSensor.name.trim(),
        imei: this.newSensor.imei.trim(),
        mqttTopic: this.newSensor.mqttTopic.trim()
      };
      
      console.log('[提交] 添加传感器请求:', {
        payload,
        tokenInfo: {
          hasToken: !!localStorage.getItem('jwtToken'),
          userType: localStorage.getItem('userType'),
          userRole: localStorage.getItem('userRole'),
          jwtLocationId: tokenValidation.payload?.locationId
        }
      });
      
      request.post('sensors/add', payload, {
        headers: { 'Content-Type': 'application/json' }
      }).then(res => {
        if (res.data.code === 200) {
          this.showAddSensorDialog = false;
          alert('添加成功');
          this.getSensors();
        } else {
          alert(res.data.message || '添加失败');
        }
      }).catch(error => {
        console.error('添加传感器错误:', error);
        if (error.response) {
          const status = error.response.status;
          const data = error.response.data || {};
          const message = data.message || '添加失败';
          
          // 详细日志，帮助调试
          console.error('[添加传感器] 错误详情:', {
            status,
            message,
            responseData: data,
            requestPayload: payload,
            currentToken: localStorage.getItem('jwtToken')?.substring(0, 50) + '...',
            userType: localStorage.getItem('userType'),
            userRole: localStorage.getItem('userRole'),
            locationId: localStorage.getItem('locationId')
          });
          
          if (status === 401) {
            // 401错误：token无效或过期
            // 注意：响应拦截器会自动清除登录信息并跳转，这里只显示错误提示
            alert('认证失败：' + message + '\n\n可能的原因：\n1. Token已过期\n2. Token格式不正确\n3. 后端无法验证Token签名\n\n请重新登录后重试。');
          } else if (status === 403) {
            alert('权限不足：' + message);
          } else {
            alert('添加失败：' + message);
          }
        } else {
          alert('网络错误，添加失败');
        }
      });
    },
    getSensors() {
      // 根据后端文档，使用 GET /api/sensors/get
      // 后端根据 JWT 中的身份自动过滤数据，要求在 Header 携带 Authorization: Bearer <token>
      // 后端新增: 当超级管理员提供 locationId 时，只返回该客户的设备
      const token = localStorage.getItem('jwtToken') || localStorage.getItem('authToken');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      // 解析当前激活的 locationId（客户ID）
      const activeLocationId = this.resolveActiveLocationId();
      const params = {};
      if (activeLocationId) {
        params.locationId = activeLocationId;
      }

      request.get('sensors/get', { headers: authHeaders, params })
        .then(res => {
          if (res.data.code === 200) {
            this.sensorGroups = res.data.data.map(sensor => {
              const voltages = { ...(sensor.voltages || { voltage1: 0 }) };
              const rawSignal = sensor.signal;
              const hasSignal = rawSignal !== null && rawSignal !== undefined && rawSignal !== '' && rawSignal !== '--';

              return {
                id: sensor.id,
                locationId: sensor.locationId,
                name: sensor.name,
                imei: sensor.imei,
                mqttTopic: sensor.mqttTopic,
                doorStatus: sensor.doorStatus,
                cardStatus: sensor.cardStatus,
                humanStatus: sensor.humanStatus,
                voltages,
                voltageLabels: { ...(sensor.voltageLabels || { label1: '' }) },
                voltageRanges: sensor.voltageRanges
                  ? { ...sensor.voltageRanges, range1: { ...(sensor.voltageRanges.range1 || { min: 0, max: 0 }) } }
                  : { range1: { min: 0, max: 0 } },
                powerVoltage: voltages.voltage1 ?? 0,
                accessControlVoltage: voltages.voltage2 ?? 0,
                externalHumanSensorVoltage: voltages.voltage3 ?? 0,
                internalHumanSensorVoltage: voltages.voltage4 ?? 0,
                createTime: sensor.createTime || '',
                updateTime: sensor.updateTime || '',
                signal: hasSignal ? rawSignal : '--',
                battery: sensor.battery,
                in1Status: sensor.In1,
                in2Status: sensor.In2,
                // 在线判定：仅依据信号是否为 "--"
                isOffline: !hasSignal
              };
            });
          } else {
            this.sensorGroups = [];
          }
        })
        .catch(() => {
          this.sensorGroups = [];
        });
    },
    validateSensors(sensors) {
      sensors.forEach(sensor => {
        // 校对 ID、名称、IMEI 和 MQTT Topic
        console.log(`ID: ${sensor.id}, 名称: ${sensor.name}, IMEI: ${sensor.imei}, MQTT Topic: ${sensor.mqttTopic}`);
        // 这里可以添加更多的校对逻辑，比如与当前传感器数据进行比较
      });
    },
    // 离线接口判断已取消，改用信号值判定 isOffline（信号<=5 视为离线）
    setupWebSocket() {
      console.log('[WebSocket] ========================================');
      console.log('[WebSocket] setupWebSocket() 方法被调用！');
      console.log('[WebSocket] ========================================');
      
      // 如果已有连接，先关闭
      if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
        console.log('[WebSocket] 关闭现有连接');
        this.socket.close();
      }
      
      // 获取JWT token
      const token = localStorage.getItem('jwtToken') || localStorage.getItem('authToken');
      if (!token) {
        console.error('[WebSocket] 未找到JWT Token，无法建立连接');
        console.error('[WebSocket] localStorage内容:', {
          jwtToken: localStorage.getItem('jwtToken'),
          authToken: localStorage.getItem('authToken'),
          userType: localStorage.getItem('userType'),
          userRole: localStorage.getItem('userRole')
        });
        return;
      }
      
      // 验证token是字符串类型
      if (typeof token !== 'string') {
        console.error('[WebSocket] Token类型错误，期望string，实际:', typeof token, token);
        return;
      }
      
      // 检查token是否过期
      try {
        const jwtPayload = this.parseJwtPayload(token);
        if (jwtPayload && jwtPayload.exp) {
          const now = Math.floor(Date.now() / 1000);
          if (jwtPayload.exp < now) {
            console.error('[WebSocket] Token已过期，请重新登录');
            alert('登录已过期，请重新登录');
            localStorage.clear();
            this.$router.replace('/login');
            return;
          }
          console.log('[WebSocket] Token验证通过，剩余有效期:', Math.floor((jwtPayload.exp - now) / 60), '分钟');
        }
      } catch (error) {
        console.warn('[WebSocket] Token解析失败，但继续尝试连接:', error);
      }
      
      // 将token作为查询参数添加到WebSocket URL中
      // 后端会在握手阶段从URL参数中读取并验证token
      // 注意：JWT本身是Base64URL编码，已经是URL安全的，不需要再编码
      const wsUrl = `${WS_BASE_URL}?token=${token}`;
      
      // 输出完整的连接信息（用于调试）
      console.log('[WebSocket] ========== 连接信息 ==========');
      console.log('[WebSocket] 基础URL:', WS_BASE_URL);
      console.log('[WebSocket] 原始Token:', token);
      console.log('[WebSocket] Token类型:', typeof token);
      console.log('[WebSocket] Token长度:', token.length);
      console.log('[WebSocket] Token前缀:', token.substring(0, 30) + '...');
      console.log('[WebSocket] Token后缀:', '...' + token.substring(token.length - 30));
      console.log('[WebSocket] 完整URL:', wsUrl);
      console.log('[WebSocket] URL长度:', wsUrl.length);
      console.log('[WebSocket] 注意: Token未编码（JWT本身已是Base64URL编码）');
      console.log('[WebSocket] 用户类型:', localStorage.getItem('userType'));
      console.log('[WebSocket] 位置ID:', localStorage.getItem('locationId'));
      console.log('[WebSocket] =============================');
      
      // 验证URL格式
      try {
        const urlObj = new URL(wsUrl);
        console.log('[WebSocket] URL解析成功:', {
          protocol: urlObj.protocol,
          host: urlObj.host,
          pathname: urlObj.pathname,
          search: urlObj.search,
          tokenParam: urlObj.searchParams.get('token') ? '存在' : '不存在',
          tokenParamLength: urlObj.searchParams.get('token')?.length || 0
        });
      } catch (error) {
        console.error('[WebSocket] URL格式验证失败:', error);
      }
      
      try {
        // 输出详细的连接参数
        console.log('[WebSocket] ========== 准备建立连接 ==========');
        console.log('[WebSocket] 将要连接的URL:', wsUrl);
        console.log('[WebSocket] 当前页面Origin:', window.location.origin);
        console.log('[WebSocket] 当前页面URL:', window.location.href);
        console.log('[WebSocket] User-Agent:', navigator.userAgent);
        console.log('[WebSocket] ===========================================');
        
        // 创建WebSocket连接，token通过URL查询参数传递
        console.log('[WebSocket] 正在创建WebSocket连接...');
        this.socket = new WebSocket(wsUrl);
        
        // 记录WebSocket对象的URL（用于验证）
        console.log('[WebSocket] WebSocket对象URL:', this.socket.url);
        console.log('[WebSocket] WebSocket对象创建成功，等待连接...');
        
        this.socket.onopen = () => {
          console.log('[WebSocket] ✅ 连接成功建立！', {
            url: this.socket.url,
            readyState: this.socket.readyState,
            protocol: this.socket.protocol,
            extensions: this.socket.extensions,
            userType: localStorage.getItem('userType'),
            locationId: localStorage.getItem('locationId')
          });
        };
        
        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            // 处理心跳包：收到heartbeat时回复pong
            if (data.type === 'heartbeat') {
              console.log('[WebSocket] 收到心跳包，回复pong');
              try {
                this.socket.send(JSON.stringify({ type: 'pong' }));
              } catch (error) {
                console.warn('[WebSocket] 发送pong失败:', error);
              }
              return;
            }

            // 处理开关门超时报警
            if (data.type === 'doorTimeoutAlert') {
              console.log('[WebSocket] 收到开关门超时报警:', data);
              this.showAlert(data.message || '开关门超时报警', 'warning');
              return;
            }

            // 处理设备数据
            // 根据后端文档，数据格式可能是：
            // { "imei": "...", "data": {...}, "timestamp": "..." }
            // 或者扁平化格式：{ "imei": "...", "in1": 1, "aiv1": 1200, ... }
            if (data.imei) {
              // 如果数据有嵌套的data字段，则展开它
              const sensorData = data.data ? { ...data, ...data.data } : data;
              this.updateSensorData(sensorData);
            } else {
              console.warn('[WebSocket] 收到未知格式的消息:', data);
            }
          } catch (error) {
            console.warn("[WebSocket] 解析消息失败:", error, "原始数据:", event.data);
          }
        };
        
        this.socket.onerror = (error) => {
          const actualUrl = this.socket?.url || wsUrl;
          console.error('[WebSocket] ❌ 连接错误:', {
            url: actualUrl,
            error: error,
            errorType: error.type,
            errorTarget: error.target,
            readyState: this.socket?.readyState,
            readyStateText: this.getReadyStateText(this.socket?.readyState),
            userType: localStorage.getItem('userType'),
            locationId: localStorage.getItem('locationId')
          });
          
          // 输出实际使用的URL，用于与APIPOST对比
          console.error('[WebSocket] 实际使用的URL:', actualUrl);
          if (actualUrl.includes('token=')) {
            const urlObj = new URL(actualUrl);
            const tokenParam = urlObj.searchParams.get('token');
            console.error('[WebSocket] URL中的token参数:', {
              exists: !!tokenParam,
              length: tokenParam?.length || 0,
              prefix: tokenParam?.substring(0, 30) + '...' || 'N/A',
              matchesOriginal: tokenParam === token
            });
          } else {
            console.error('[WebSocket] ⚠️ URL中未找到token参数！');
          }
          
          // 提供诊断建议
          console.error('[WebSocket] 诊断建议:');
          console.error('1. 检查后端WebSocket服务是否运行在 121.41.68.114:8088');
          console.error('2. 检查后端是否正确实现了从URL查询参数读取token');
          console.error('3. 检查网络连接和防火墙设置');
          console.error('4. 检查后端日志，查看是否有token验证错误');
          console.error('5. 对比APIPOST中使用的URL格式，确保前端URL格式一致');
        };
        
        this.socket.onclose = (event) => {
          const closeCodeMessages = {
            1000: '正常关闭',
            1001: '端点离开（服务器关闭或浏览器导航）',
            1002: '协议错误',
            1003: '不支持的数据类型',
            1006: '异常关闭（无法建立连接或连接丢失）',
            1007: '数据格式错误',
            1008: '策略违规',
            1009: '消息过大',
            1010: '扩展协商失败',
            1011: '服务器内部错误'
          };
          
          const actualUrl = this.socket?.url || wsUrl;
          console.warn('[WebSocket] ⚠️ 连接关闭:', {
            code: event.code,
            codeMessage: closeCodeMessages[event.code] || '未知错误码',
            reason: event.reason || '(无原因)',
            wasClean: event.wasClean,
            url: actualUrl
          });
          
          // 输出实际使用的URL，用于与APIPOST对比
          console.warn('[WebSocket] 连接关闭时的URL:', actualUrl);
          if (actualUrl.includes('token=')) {
            try {
              const urlObj = new URL(actualUrl);
              const tokenParam = urlObj.searchParams.get('token');
              console.warn('[WebSocket] URL中的token参数:', {
                exists: !!tokenParam,
                length: tokenParam?.length || 0,
                prefix: tokenParam?.substring(0, 30) + '...' || 'N/A'
              });
            } catch (e) {
              console.warn('[WebSocket] URL解析失败:', e);
            }
          } else {
            console.error('[WebSocket] ⚠️ URL中未找到token参数！');
          }
          
          // 根据错误码提供具体建议
          if (event.code === 1006) {
            console.error('[WebSocket] 错误码1006通常表示:');
            console.error('- 后端服务未运行或无法访问');
            console.error('- 后端在握手阶段拒绝了连接（可能是token验证失败）');
            console.error('- 网络连接问题');
            console.error('请检查后端日志，确认是否收到连接请求以及token验证结果');
            console.error('请对比APIPOST中成功连接的URL格式，确保前端URL格式完全一致');
          }
          
          // 如果不是正常关闭，尝试重连（延迟5秒）
          if (event.code !== 1000) {
            console.log('[WebSocket] 将在5秒后尝试重连...');
            setTimeout(() => {
              if (this.$route.name === 'StatePage') {
                this.setupWebSocket();
              }
            }, 5000);
          }
        };
      } catch (error) {
        console.error('[WebSocket] ❌ 创建连接失败:', error);
        console.error('[WebSocket] 错误详情:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
      }
    },
    getReadyStateText(state) {
      const states = {
        0: 'CONNECTING',
        1: 'OPEN',
        2: 'CLOSING',
        3: 'CLOSED'
      };
      return states[state] || 'UNKNOWN';
    },
    /**
     * 解析JWT token，提取payload中的信息
     */
    parseJwtPayload(token) {
      try {
        if (!token || typeof token !== 'string') {
          return null;
        }
        // JWT格式：Header.Payload.Signature
        const parts = token.split('.');
        if (parts.length !== 3) {
          console.warn('[JWT解析] Token格式不正确，部分数量:', parts.length);
          return null;
        }
        // 解码payload（base64url）
        const payload = parts[1];
        // base64url解码：将'-'替换为'+'，'_'替换为'/'，然后添加padding
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
        
        // 使用正确的方式解码UTF-8字符（支持中文）
        let decoded;
        try {
          // 方法1：使用decodeURIComponent和escape处理UTF-8
          decoded = decodeURIComponent(escape(atob(padded)));
        } catch (e) {
          // 如果方法1失败，尝试直接使用atob（可能不包含中文）
          try {
            decoded = atob(padded);
          } catch (e2) {
            throw new Error('Base64解码失败: ' + e2.message);
          }
        }
        
        const parsed = JSON.parse(decoded);
        return parsed;
      } catch (error) {
        console.error('[JWT解析] Token解析失败:', error, 'Token前缀:', token?.substring(0, 50));
        return null;
      }
    },
    updateSensorData(data) {
      const sensor = this.sensorGroups.find(s => s.imei === data.imei);
      if (sensor) {
        // 信号和电池
        if (typeof data.csq === 'number') {
          sensor.signal = data.csq;
        }
        if (typeof data.vbat === 'number') {
          sensor.battery = data.vbat;
        }
        // 门状态判断
        sensor.doorStatus = data.In1 === 0 ? 'open' : 'closed';
        // 其它字段赋值
        if (typeof data.aiv1 === 'number') sensor.powerVoltage = data.aiv1; // 供电
        if (typeof data.aiv2 === 'number') sensor.accessControlVoltage = data.aiv2; // 门禁
        if (typeof data.aiv3 === 'number') sensor.externalHumanSensorVoltage = data.aiv3; // 外感应
        if (typeof data.aiv4 === 'number') sensor.internalHumanSensorVoltage = data.aiv4; // 内感应
        // 门禁卡状态
        sensor.cardStatus = data.In3 === 0 ? 'active' : 'inactive';
        // 人体感应器状态
        sensor.humanStatus = data.In4 === 0 ? 'active' : 'inactive';
        // 新增字段赋值
        sensor.in1Status = data.In1;
        sensor.in2Status = data.In2;

        // 重新判定在线状态：信号为 "--" 视为离线
        const hasSignal = sensor.signal !== null && sensor.signal !== undefined && sensor.signal !== '' && sensor.signal !== '--';
        sensor.isOffline = !hasSignal;
      }
    },
    submitEditSensor() {
      const s = this.editSensorDialog.sensor;
      
      // 验证token
      const tokenValidation = this.validateJWTToken();
      if (!tokenValidation.valid) {
        alert('Token验证失败：' + tokenValidation.reason + '\n\n请重新登录后重试。');
        if (tokenValidation.reason.includes('过期')) {
          localStorage.clear();
          this.$router.replace('/login');
        }
        return;
      }
      
      if (!s.name || !s.imei || !s.mqttTopic) {
        alert('请填写完整信息');
        return;
      }
      if (s.imei.length > 15) {
        alert('IMEI编号不能超过15位');
        return;
      }
      
      // 确保locationId是数字类型
      let locationId = Number(s.locationId);
      if (!Number.isFinite(locationId) || locationId <= 0) {
        alert('客户位置ID无效');
        return;
      }
      
      // 验证请求中的locationId是否与JWT中的一致（普通用户只能操作自己的位置）
      if (tokenValidation.payload && tokenValidation.payload.locationId) {
        const jwtLocationId = Number(tokenValidation.payload.locationId);
        if (jwtLocationId !== locationId) {
          console.warn('[编辑] locationId不匹配，使用JWT中的locationId:', {
            jwtLocationId,
            requestLocationId: locationId
          });
          if (localStorage.getItem('userType') === 'location') {
            locationId = jwtLocationId;
          }
        }
      }
      
      // 根据后端文档，发送 Map 结构（后端期望 LinkedHashMap 而非数组）
      const normalizeRangesToObject = (ranges) => {
        if (!ranges) return {};
        if (!Array.isArray(ranges)) return ranges;
        const obj = {};
        ranges.forEach((r, idx) => {
          obj[`channel${idx + 1}`] = {
            min: Number(r.min ?? 0),
            max: Number(r.max ?? 0),
            label: r.label || r.name || ''
          };
        });
        return obj;
      };
      const normalizeLabelsToObject = (labels) => {
        if (!labels) return {};
        if (!Array.isArray(labels)) return labels;
        const obj = {};
        labels.forEach((label, idx) => {
          obj[`channel${idx + 1}`] = label ?? '';
        });
        return obj;
      };
      const normalizeVoltagesToObject = (volts) => {
        if (!volts) return {};
        if (!Array.isArray(volts)) {
          const obj = {};
          Object.keys(volts).forEach((k) => {
            obj[k] = Number(volts[k] ?? 0);
          });
          return obj;
        }
        const obj = {};
        volts.forEach((v, idx) => {
          obj[`channel${idx + 1}`] = Number(v ?? 0);
        });
        return obj;
      };

      const payload = {
        locationId: locationId ? Number(locationId) : null,
        name: (s.name || '').trim(),
        imei: (s.imei || '').trim(),
        mqttTopic: (s.mqttTopic || '').trim(),
        doorStatus: s.doorStatus ?? null,
        cardStatus: s.cardStatus ?? null,
        humanStatus: s.humanStatus ?? null,
        voltageRanges: normalizeRangesToObject(s.voltageRanges),
        voltageLabels: normalizeLabelsToObject(s.voltageLabels),
        voltages: normalizeVoltagesToObject(s.voltages)
      };
      
      // 确保所有字段都是有效的
      if (!payload.name || !payload.imei || !payload.mqttTopic) {
        alert('请确保所有必填字段都已填写');
        return;
      }
      
      console.log('[编辑传感器] 提交数据:', {
        sensorId: s.id,
        url: `sensors/update/${s.id}`,
        payload: JSON.stringify(payload),
        payloadObject: payload,
        originalData: {
          locationId: s.locationId,
          name: s.name,
          imei: s.imei,
          mqttTopic: s.mqttTopic,
          doorStatus: s.doorStatus,
          cardStatus: s.cardStatus,
          humanStatus: s.humanStatus,
          voltages: s.voltages,
          voltageLabels: s.voltageLabels,
          voltageRanges: s.voltageRanges
        },
        tokenInfo: {
          hasToken: !!localStorage.getItem('jwtToken'),
          userType: localStorage.getItem('userType'),
          userRole: localStorage.getItem('userRole'),
          jwtLocationId: tokenValidation.payload?.locationId
        }
      });
      
      // 确保使用正确的请求方法
      request.put(`sensors/update/${s.id}`, payload, {
        headers: { 
          'Content-Type': 'application/json'
        }
      }).then(res => {
        console.log('[编辑传感器] 完整响应:', {
          status: res.status,
          statusText: res.statusText,
          headers: res.headers,
          data: res.data,
          requestUrl: res.config?.url,
          requestMethod: res.config?.method,
          requestData: res.config?.data
        });
        
        if (res.data.code === 200) {
          // 检查响应中是否包含更新后的数据
          if (res.data.data) {
            console.log('[编辑传感器] 后端返回的更新后数据:', res.data.data);
          }
          this.editSensorDialog.visible = false;
          alert('修改成功');
          // 刷新传感器列表以获取最新数据
          this.getSensors();
        } else {
          console.warn('[编辑传感器] 后端返回非200状态:', res.data);
          alert(res.data.message || '修改失败');
        }
      }).catch(error => {
        console.error('[编辑传感器] 错误详情:', {
          error,
          response: error.response?.data,
          status: error.response?.status,
          requestPayload: payload,
          requestUrl: `sensors/update/${s.id}`
        });
        
        if (error.response) {
          const status = error.response.status;
          const data = error.response.data || {};
          const message = data.message || '修改失败';
          
          if (status === 401) {
            alert('认证失败：' + message + '\n\n请重新登录后重试。');
          } else if (status === 403) {
            alert('权限不足：' + message);
          } else {
            alert('修改失败：' + message);
          }
        } else {
          alert('网络错误，修改失败');
        }
      });
    },
    showMap(location) {
      this.currentLocation = location;
      this.mapVisible = true;
    }
  },
  watch: {
    '$route.params.locationId': 'loadLocationData',
    sensorGroups: {
      handler() {
        this.checkAlerts();
      },
      deep: true
    },
    '$route.params.sensorId': {
      handler() {
        // 路由参数变化时重新加载传感器数据
        this.getSensors();
        // 重新建立WebSocket连接
        if (this.socket) {
          this.socket.close();
        }
        this.setupWebSocket();
      },
      immediate: false
    }
  },
  mounted() {
    console.log('[State.vue] mounted() 被调用');
    console.log('[State.vue] 准备初始化 WebSocket...');
    
    if (this.isAdmin) {
      this.getHospitals();
    }
    this.getSensors(); // 获取传感器信息
    
    console.log('[State.vue] 即将调用 setupWebSocket()');
    this.setupWebSocket(); // 设置 WebSocket 连接
    console.log('[State.vue] setupWebSocket() 调用完成');

    // 启动轮询
    this.alertPollingInterval = setInterval(() => {
      this.checkAlerts();
    }, 30000); // 每 30 秒检查一次报警
  }
}
</script>

<style scoped>
.state-page {
  padding: 20px;
}

.chart-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 50px;
  padding: 20px;
}

.sensor-card {
  background: linear-gradient(135deg, #181f3a 80%, #1a1f4b 100%);
  border: 1.5px solid #00f3ff88;
  border-radius: 18px;
  box-shadow: 0 0 24px #00f3ff22, 0 2px 16px #000a;
  padding: 18px 20px 16px 20px;
  margin: 0 auto 24px auto;
  min-width: 320px;
  max-width: 370px;
  color: #eaf6ff;
  position: relative;
  transition: box-shadow 0.3s;
}

.sensor-card:hover {
  box-shadow: 0 0 32px #00f3ff66, 0 2px 24px #000c;
  border-color: #00f3ff;
}

.sensor-card.offline {
  border-color: #ff6b6b;
  box-shadow: 0 0 24px #ff6b6b55, 0 2px 16px #000a;
  filter: grayscale(0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.1em;
  margin-bottom: 8px;
}

.sensor-id {
  color: #00f3ff;
  font-weight: bold;
  font-size: 1em;
}

.sensor-name {
  flex: 1;
  text-align: right;
  font-size: 1.2em;
  font-weight: 600;
  letter-spacing: 1px;
  color: #fff;
}

.card-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 10px;
}

.status-badge {
  margin-left: 10px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
}
.offline-badge {
  background: rgba(255, 107, 107, 0.15);
  color: #ff6b6b;
  border: 1px solid rgba(255, 107, 107, 0.4);
}
.online-badge {
  background: rgba(0, 243, 255, 0.12);
  color: #00f3ff;
  border: 1px solid rgba(0, 243, 255, 0.5);
}

.info-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.98em;
}

.info-icon {
  color: #00f3ff;
  font-size: 1.1em;
}

.info-label {
  color: #7eeeff;
  font-size: 0.95em;
}

.info-value {
  color: #fff;
  font-size: 0.97em;
  word-break: break-all;
}

.card-voltage {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  margin-bottom: 10px;
  margin-top: 2px;
}

.voltage-item {
  min-width: 120px;
  font-size: 0.97em;
  color: #b6eaff;
  display: flex;
  justify-content: space-between;
}

.voltage-label {
  color: #00f3ff;
  margin-right: 4px;
}

.voltage-value {
  color: #fff;
}

.card-status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px 18px;
  margin-top: 10px;
  position: relative;
  row-gap: 12px;
  column-gap: 18px;
  /* 保证每行最多3个 */
  max-width: 330px;
  margin-left: auto;
  margin-right: auto;
}

.status-block {
  display: flex;
  align-items: center;
  position: relative;
  min-width: 90px;
  background: rgba(0,243,255,0.06);
  border-radius: 8px;
  padding: 2px 8px 2px 4px;
  box-shadow: 0 0 8px #00f3ff22;
  margin-bottom: 0;
}

.status-led {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  margin-right: 4px;
  box-shadow: 0 0 8px #00f3ff88;
  background: #2ed573;
  border: 1.5px solid #fff2;
}

.status-led.led-closed {
  background: #ff4757;
  box-shadow: 0 0 8px #ff4757cc;
}

.status-led.led-open {
  background: #2ed573;
  box-shadow: 0 0 8px #2ed573cc;
}

.status-icon {
  font-size: 1.5em;
  margin: 0 4px;
  color: #00f3ff;
}

.status-label {
  color: #fff;
  font-size: 0.95em;
  margin-left: 2px;
}

.status-x {
  color: #ff4757;
  font-size: 1.1em;
  position: absolute;
  right: 2px;
  top: -8px;
  font-weight: bold;
  pointer-events: none;
  text-shadow: 0 0 6px #ff4757cc;
}

.settings-icon {
  cursor: pointer;
  color: #00f3ff;
  font-size: 1.7em;
  margin-left: 10px;
  transition: color 0.2s;
}

.settings-icon:hover {
  color: #fff;
  text-shadow: 0 0 8px #00f3ff;
}

.alert-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #ff4757;
  color: white;
  padding: 10px;
  text-align: center;
  z-index: 2000;
}

.alert-message {
  flex: 1;
}

.alert-counter {
  font-size: 0.8em;
  opacity: 0.7;
}

@keyframes alert-slide {
  0% { transform: translateY(100%); }
  10% { transform: translateY(0); }
  90% { transform: translateY(0); }
  100% { transform: translateY(100%); }
}

.chart-group.add-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 2px dashed rgba(0, 243, 255, 0.4);
  background: rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 240px;
}

.chart-group.add-card:hover {
  background: rgba(0, 243, 255, 0.1);
}

.add-icon {
  font-size: 3em;
  color: rgba(0, 243, 255, 0.6);
  margin-bottom: 10px;
  transition: color 0.3s ease;
}

.chart-group.add-card:hover .add-icon {
  color: #00f3ff;
}

.add-text {
  font-size: 1.1em;
  color: rgba(0, 243, 255, 0.6);
  transition: color 0.3s ease;
}

.chart-group.add-card:hover .add-text {
  color: #00f3ff;
}

.chart-group:not(.add-card) {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 243, 255, 0.2);
  border-radius: 8px;
  padding: 15px;
  position: relative;
  min-height: 220px;
  height: 240px;
  max-height: 260px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.location-name-display {
  font-size: 1.8em;
  color: #00f3ff;
  text-align: center;
  margin-bottom: 20px;
  text-shadow: 0 0 10px rgba(0, 243, 255, 0.5);
}

.corner-status {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.95em;
  padding: 4px 10px;
  border-radius: 12px;
  background: rgba(0, 243, 255, 0.10);
  box-shadow: 0 0 8px #00f3ff33;
  z-index: 2;
}

.left-bottom {
  left: 10px;
  bottom: 10px;
}

.right-bottom {
  right: 10px;
  bottom: 10px;
}

.card-icon, .human-icon {
  font-size: 1.3em;
}

.card-icon.active, .human-icon.active {
  color: #2ed573;
}

.card-icon.inactive, .human-icon.inactive {
  color: #ff4757;
}

.delete-btn {
  background: #ff4757;
  color: white;
  margin-left: 10px;
}

.dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.dialog {
  background: #1a1f4b;
  padding: 20px;
  border-radius: 8px;
  width: 600px;
  color: white;
}

.dialog h3 {
  color: #00f3ff;
  margin-bottom: 20px;
}

.dialog input[type="text"] {
  width: 100%;
  padding: 8px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid #00f3ff;
  color: white;
  border-radius: 4px;
}

.dialog .dialog-actions {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.dialog .dialog-actions button {
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.dialog .dialog-actions button:first-child {
  background: #00f3ff;
  color: #1a1f4b;
}

.dialog .dialog-actions button:last-child {
  background: #ff4757;
  color: white;
}

.map-container {
  width: 100%;
  height: 400px;
}
</style> 